import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Rocket, Share2, Upload, Clock, Users, Trophy, Copy, Loader2, Ticket, Crown,
  Facebook, Instagram, Music2, CheckCircle2, XCircle, Coins, Zap, Lock, Award, Gift,
} from "lucide-react";
import Btn from "../components/Btn";
import CertificateModal from "../components/CertificateModal";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";

function timeLeft(iso) {
  const diff = Math.max(0, new Date(iso) - new Date());
  return { d: Math.floor(diff / 86400000), h: Math.floor((diff / 3600000) % 24), m: Math.floor((diff / 60000) % 60) };
}

export default function MegaTryout() {
  const [events, setEvents] = useState(null);
  const [contact, setContact] = useState({});
  const [paidCost, setPaidCost] = useState({ token: 20, energy: 50 });

  const load = async () => {
    try {
      const { data } = await api.get("/mega/events");
      setEvents(data.events || []);
    } catch { setEvents([]); }
  };
  useEffect(() => {
    load();
    api.get("/site-settings").then(({ data }) => setContact(data)).catch(() => {});
    api.get("/costs").then(({ data }) => { if (data.costs?.mega_paid) setPaidCost(data.costs.mega_paid); }).catch(() => {});
  }, []);

  if (!events) return <div className="min-h-[50vh] grid place-items-center"><Loader2 className="w-10 h-10 animate-spin text-su-primary" /></div>;

  if (events.length === 0) return (
    <div className="min-h-[50vh] grid place-items-center text-center px-4" data-testid="mega-empty">
      <div>
        <Rocket className="w-12 h-12 text-su-muted mx-auto mb-3" />
        <h1 className="font-heading font-black text-xl mb-1">Belum ada Tryout Akbar</h1>
        <p className="text-su-muted font-medium">Nantikan event akbar berikutnya ya, pejuang! 🚀</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-8" data-testid="mega-list">
      <div>
        <h1 className="font-heading font-black text-2xl sm:text-3xl flex items-center gap-2"><Rocket className="w-7 h-7 text-su-danger" /> Tryout Akbar</h1>
        <p className="text-su-muted font-medium">Ikuti simulasi serentak se-Indonesia & panjat peringkat nasional.</p>
      </div>
      {events.map((ev) => <EventCard key={ev.event_id} event={ev} contact={contact} paidCost={paidCost} onChange={load} />)}
    </div>
  );
}

function EventCard({ event, contact = {}, paidCost = { token: 20, energy: 50 }, onChange }) {
  const [board, setBoard] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [joining, setJoining] = useState(false);
  const [paying, setPaying] = useState(false);
  const [sharedDone, setSharedDone] = useState(false);
  const [storyPublic, setStoryPublic] = useState(false);
  const [followedIg, setFollowedIg] = useState(false);
  const [followedTiktok, setFollowedTiktok] = useState(false);
  const [followedFb, setFollowedFb] = useState(false);
  const fileRef = useRef(null);
  const [cert, setCert] = useState(null);
  const nav = useNavigate();
  const { refresh, user } = useAuth();

  const unlockPaid = async () => {
    setPaying(true);
    try {
      await api.post(`/mega/${event.event_id}/unlock-paid`);
      await refresh?.();
      toast.success("Tiket dibuka! Kamu langsung bisa ikut Tryout Akbar 🎟️");
      onChange();
    } catch (err) {
      if (err?.response?.status === 402) {
        toast.error(apiErr(err) + " — mengarahkan ke halaman pembelian...");
        setTimeout(() => nav("/pricing"), 1200);
      } else {
        toast.error(apiErr(err));
      }
    } finally { setPaying(false); }
  };

  useEffect(() => {
    api.get(`/mega/${event.event_id}/leaderboard`).then(({ data }) => setBoard(data.leaderboard)).catch(() => {});
  }, [event.event_id]);

  const t = timeLeft(event.scheduled_at);
  const allDone = sharedDone && storyPublic && followedIg && followedTiktok;
  const reentry = event.has_participated && !event.can_free;
  const dl = event.deadline_at ? timeLeft(event.deadline_at) : null;
  const ended = event.deadline_passed;

  const igHandle = (contact.instagram || "@skorup.io").replace(/^@/, "");
  const ttHandle = (contact.tiktok || "@skorup.io").replace(/^@/, "");
  const fbHandle = (contact.facebook || "SkorUp.io").replace(/^@/, "");

  const followIg = () => { window.open(`https://instagram.com/${igHandle}`, "_blank"); setFollowedIg(true); toast.success("Terima kasih sudah follow Instagram! 📸"); };
  const followTt = () => { window.open(`https://www.tiktok.com/@${ttHandle}`, "_blank"); setFollowedTiktok(true); toast.success("Terima kasih sudah follow TikTok! 🎵"); };
  const followFb = () => { window.open(`https://www.facebook.com/${fbHandle}`, "_blank"); setFollowedFb(true); toast.success("Terima kasih sudah follow Facebook! 👍"); };

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (files.length > 6) { toast.error("Maksimal 6 gambar bukti."); return; }
    if (!allDone) { toast.error("Selesaikan semua syarat repost & follow dulu ya!"); return; }
    setUploading(true);
    try {
      let subId = "";
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        if (subId) fd.append("submission_id", subId);
        fd.append("reposted_story", "true");
        fd.append("story_public", "true");
        fd.append("followed_ig", "true");
        fd.append("followed_tiktok", "true");
        fd.append("followed_fb", followedFb ? "true" : "false");
        const { data } = await api.post(`/mega/${event.event_id}/proof`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        subId = data.id;
      }
      toast.success(`${files.length} bukti terkirim! Menunggu verifikasi admin.`);
      onChange();
    } catch (err) { toast.error(apiErr(err)); } finally { setUploading(false); if (e.target) e.target.value = ""; }
  };

  const join = async () => {
    setJoining(true);
    try {
      const { data } = await api.post(`/mega/${event.event_id}/join`);
      nav(`/cbt/${data.attempt_id}`, { state: data });
    } catch (err) { toast.error(apiErr(err)); setJoining(false); }
  };

  const copyCaption = () => { navigator.clipboard?.writeText(event.share_caption); toast.success("Caption disalin!"); };
  const shareUrl = window.location.origin;
  const shareWA = () => window.open(`https://wa.me/?text=${encodeURIComponent(event.share_caption + " " + shareUrl)}`, "_blank");
  const shareTW = () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(event.share_caption)}&url=${encodeURIComponent(shareUrl)}`, "_blank");
  const shareFB = () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(event.share_caption)}`, "_blank");
  const shareIG = () => { navigator.clipboard?.writeText(event.share_caption + " " + shareUrl); toast.success("Caption disalin! Tempel di Instagram Story/postingan kamu 📸"); window.open("https://www.instagram.com/", "_blank"); };
  const shareTT = () => { navigator.clipboard?.writeText(event.share_caption + " " + shareUrl); toast.success("Caption disalin! Tempel di caption TikTok kamu 🎵"); window.open("https://www.tiktok.com/upload", "_blank"); };

  return (
    <div className="space-y-4" data-testid={`mega-event-${event.event_id}`}>
      <div className="card-chunky overflow-hidden">
        <div className="relative h-40 sm:h-52 bg-su-bg">
          {event.poster_url && <img src={event.poster_url} alt={event.title} className="w-full h-full object-cover" />}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          <div className="absolute bottom-0 p-5 text-white">
            <span className="inline-flex items-center gap-1.5 bg-su-danger text-white px-3 py-1 rounded-full font-heading font-black text-xs mb-2"><Rocket className="w-3.5 h-3.5" /> TRYOUT AKBAR</span>
            <h2 className="font-heading font-black text-2xl sm:text-3xl">{event.title}</h2>
            <p className="font-medium text-white/80 text-sm">{event.subtitle}</p>
          </div>
        </div>
        <div className="p-5 grid grid-cols-3 gap-3">
          <Stat icon={Clock} label="Mulai dalam" value={`${t.d}h ${t.h}j ${t.m}m`} />
          <Stat icon={Users} label="Peserta" value={event.participants} />
          <Stat icon={Trophy} label="Ujian" value={event.exam} />
        </div>
        {dl && (
          <div className={`px-5 py-3 border-t-2 border-su-border text-center font-heading font-black text-sm flex items-center justify-center gap-2 ${ended ? "text-su-muted" : "text-su-danger"}`} data-testid={`mega-deadline-banner-${event.event_id}`}>
            <Clock className="w-4 h-4" />
            {ended ? "Waktu pengerjaan sudah berakhir" : `Sisa waktu pengerjaan: ${dl.d}h ${dl.h}j ${dl.m}m`}
          </div>
        )}
      </div>

      {ended && !event.resume ? (
        <div className="card-chunky p-6 text-center" data-testid={`mega-ended-${event.event_id}`}>
          <div className="w-16 h-16 rounded-2xl bg-su-muted/15 grid place-items-center mx-auto mb-3"><Clock className="w-8 h-8 text-su-muted" /></div>
          <h3 className="font-heading font-black text-xl mb-1">Waktu pengerjaan sudah berakhir ⏰</h3>
          <p className="text-su-muted font-medium">Tryout Akbar ini sudah ditutup. Lihat papan peringkat & hadiah di bawah. Nantikan event berikutnya, pejuang! 🚀</p>
        </div>
      ) : event.has_ticket ? (
        (event.is_open || event.resume) ? (
          <div className="card-chunky p-6 text-center">
            <div className="w-16 h-16 rounded-2xl bg-su-success/15 grid place-items-center mx-auto mb-3"><Ticket className="w-8 h-8 text-su-success" /></div>
            <h3 className="font-heading font-black text-xl mb-1">{event.resume ? "Lanjutkan tryout-mu ⏱️" : "Tiketmu aktif! 🎟️"}</h3>
            <p className="text-su-muted font-medium mb-2">{event.resume ? "Kamu punya sesi yang belum selesai. Lanjutkan dari tempat terakhir." : "Tryout Akbar sudah DIBUKA. Semangat, pejuang!"}</p>
            {!user?.vip && <p className="text-xs text-su-muted mb-4">🎟️ Tiket berlaku untuk <b>1&times;</b> pengerjaan — habis setelah kamu menyelesaikan tryout.</p>}
            <Btn data-testid={`mega-join-${event.event_id}`} onClick={join} disabled={joining} size="lg" className="w-full max-w-xs mx-auto">
              {joining ? "Menyiapkan..." : (event.resume ? "Lanjutkan Tryout Akbar ⏱️" : "Mulai Tryout Akbar 🚀")}
            </Btn>
          </div>
        ) : (
          <div className="card-chunky p-6 text-center" data-testid={`mega-locked-${event.event_id}`}>
            <div className="w-16 h-16 rounded-2xl bg-su-energy/15 grid place-items-center mx-auto mb-3"><Lock className="w-8 h-8 text-su-energy" /></div>
            <h3 className="font-heading font-black text-xl mb-1">Tiketmu aktif, tapi soal masih terkunci 🔒</h3>
            <p className="text-su-muted font-medium mb-4">Tryout Akbar dikerjakan serentak. Soal baru bisa dibuka saat admin memulai event sesuai jadwal.</p>
            <div className="inline-flex items-center gap-2 rounded-2xl bg-su-bg border-2 border-su-border px-5 py-3 font-heading font-black">
              <Clock className="w-5 h-5 text-su-primary" /> Mulai dalam {t.d}h {t.h}j {t.m}m
            </div>
            {dl && <p className="text-xs text-su-muted mt-3">⏰ Batas pengerjaan: {new Date(event.deadline_at).toLocaleString("id-ID")}</p>}
          </div>
        )
      ) : event.submission_status === "pending" ? (
        <div className="card-chunky p-6 text-center">
          <Loader2 className="w-12 h-12 text-su-energy animate-spin mx-auto mb-3" />
          <h3 className="font-heading font-black text-xl mb-1">Bukti sedang diverifikasi ⏳</h3>
          <p className="text-su-muted font-medium">Admin akan menyetujui bukti share kamu segera. Cek lagi sebentar.</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid={`mega-options-${event.event_id}`}>
          <div className="text-center px-2">
            <h3 className="font-heading font-black text-lg">{reentry ? "Mau ikut lagi? 🔁" : "Pilih cara ikut Tryout Akbar"}</h3>
            <p className="text-su-muted font-medium text-sm">{reentry ? "Tiketmu sudah terpakai. Beli tiket baru dengan token untuk mengulang Tryout Akbar." : "Buka tiketmu lewat salah satu opsi berikut."}</p>
          </div>

          {event.can_free && (
          <div className="card-chunky p-6" data-testid={`mega-share-mission-${event.event_id}`}>
          <span className="inline-flex items-center gap-1.5 bg-su-success/15 text-su-success px-3 py-1 rounded-full font-heading font-black text-xs mb-3">OPSI 1 · GRATIS 🎉</span>
          {event.submission_status === "rejected" && event.submission_comment && (
            <div className="rounded-2xl border-2 border-su-danger/40 bg-su-danger/10 p-4 mb-4" data-testid={`mega-rejected-${event.event_id}`}>
              <p className="font-heading font-black text-su-danger flex items-center gap-2"><XCircle className="w-5 h-5" /> Bukti sebelumnya ditolak</p>
              <p className="text-sm font-medium text-su-text mt-1"><span className="font-black">Catatan admin:</span> {event.submission_comment}</p>
              <p className="text-xs text-su-muted mt-1">Perbaiki sesuai catatan, lalu kirim ulang bukti di bawah ya.</p>
            </div>
          )}
          <div className="flex items-center gap-2 mb-1"><Share2 className="w-5 h-5 text-su-primary" /><h3 className="font-heading font-black text-xl">Misi Berbagi = Tiket Gratis</h3></div>
          <p className="text-su-muted font-medium text-sm mb-5">Bagikan poster event ke media sosial, lalu upload screenshot sebagai bukti. Admin approve → tiket gratis!</p>
          <div className="rounded-2xl bg-su-bg border-2 border-su-border p-4 mb-4">
            <p className="text-sm font-medium text-su-text mb-3">"{event.share_caption}"</p>
            <div className="flex flex-wrap gap-2">
              <Btn variant="ghost" size="sm" data-testid={`share-copy-${event.event_id}`} onClick={copyCaption}><Copy className="w-4 h-4" /> Salin</Btn>
              <Btn variant="success" size="sm" data-testid={`share-wa-${event.event_id}`} onClick={shareWA}>WhatsApp</Btn>
              <button data-testid={`share-fb-${event.event_id}`} onClick={shareFB} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-heading font-extrabold text-sm text-white transition-transform hover:scale-105" style={{ background: "#1877F2" }}><Facebook className="w-4 h-4 fill-current" /> Facebook</button>
              <button data-testid={`share-ig-${event.event_id}`} onClick={shareIG} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-heading font-extrabold text-sm text-white transition-transform hover:scale-105" style={{ background: "linear-gradient(45deg,#F58529,#DD2A7B,#8134AF)" }}><Instagram className="w-4 h-4" /> Instagram</button>
              <button data-testid={`share-tt-${event.event_id}`} onClick={shareTT} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-heading font-extrabold text-sm text-white bg-black transition-transform hover:scale-105"><Music2 className="w-4 h-4" /> TikTok</button>
              <button data-testid={`share-tw-${event.event_id}`} onClick={shareTW} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-heading font-extrabold text-sm text-white bg-su-text transition-transform hover:scale-105">Twitter/X</button>
            </div>
          </div>

          {(event.ig_post_url || event.tiktok_post_url || event.fb_post_url) && (
            <div className="rounded-2xl bg-su-primary/5 border-2 border-su-primary/30 p-4 mb-4" data-testid={`mega-official-posts-${event.event_id}`}>
              <p className="text-xs font-heading font-black uppercase tracking-wider text-su-primary mb-1">📌 Postingan resmi untuk di-repost</p>
              <p className="text-xs text-su-muted font-medium mb-3">Klik salah satu, lalu repost ke Story-mu (atur PUBLIK). Tidak perlu cari manual!</p>
              <div className="flex flex-wrap gap-2">
                {event.ig_post_url && (
                  <a href={event.ig_post_url} target="_blank" rel="noreferrer" data-testid={`post-ig-${event.event_id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-heading font-extrabold text-sm text-white transition-transform hover:scale-105" style={{ background: "linear-gradient(45deg,#F58529,#DD2A7B,#8134AF)" }}><Instagram className="w-4 h-4" /> Buka di Instagram</a>
                )}
                {event.tiktok_post_url && (
                  <a href={event.tiktok_post_url} target="_blank" rel="noreferrer" data-testid={`post-tt-${event.event_id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-heading font-extrabold text-sm text-white bg-black transition-transform hover:scale-105"><Music2 className="w-4 h-4" /> Buka di TikTok</a>
                )}
                {event.fb_post_url && (
                  <a href={event.fb_post_url} target="_blank" rel="noreferrer" data-testid={`post-fb-${event.event_id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-heading font-extrabold text-sm text-white transition-transform hover:scale-105" style={{ background: "#1877F2" }}><Facebook className="w-4 h-4 fill-current" /> Buka di Facebook</a>
                )}
              </div>
            </div>
          )}

          {/* syarat wajib */}
          <div className="rounded-2xl border-2 border-su-border p-4 mb-4 space-y-3" data-testid={`mega-requirements-${event.event_id}`}>
            <p className="text-xs font-heading font-black uppercase tracking-wider text-su-muted">Syarat buka tiket gratis</p>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" data-testid={`req-repost-${event.event_id}`} checked={sharedDone} onChange={(e) => setSharedDone(e.target.checked)} className="w-5 h-5 mt-0.5 accent-[var(--su-primary)] shrink-0" />
              <span className="font-heading font-bold text-sm">Aku sudah <span className="text-su-primary">repost postingan Tryout Akbar</span> SkorUp.io (pilih salah satu sosmed di atas) ke <span className="text-su-primary">Story</span>-ku</span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" data-testid={`req-public-${event.event_id}`} checked={storyPublic} onChange={(e) => setStoryPublic(e.target.checked)} className="w-5 h-5 mt-0.5 accent-[var(--su-primary)] shrink-0" />
              <span className="font-heading font-bold text-sm">Story-ku diatur <span className="text-su-primary">PUBLIK</span> (bukan Close Friends / tidak ditujukan ke orang tertentu)</span>
            </label>
            <div className="flex items-center justify-between gap-3">
              <span className="font-heading font-bold text-sm flex items-center gap-2"><Instagram className="w-4 h-4 text-su-secondaryShadow" /> Follow Instagram {contact.instagram || "@skorup.io"}</span>
              {followedIg
                ? <span className="flex items-center gap-1 text-su-success font-heading font-black text-xs" data-testid={`req-ig-done-${event.event_id}`}><CheckCircle2 className="w-4 h-4" /> Selesai</span>
                : <Btn variant="ghost" size="sm" data-testid={`req-ig-${event.event_id}`} onClick={followIg}>Follow</Btn>}
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="font-heading font-bold text-sm flex items-center gap-2"><Music2 className="w-4 h-4" /> Follow TikTok {contact.tiktok || "@skorup.io"}</span>
              {followedTiktok
                ? <span className="flex items-center gap-1 text-su-success font-heading font-black text-xs" data-testid={`req-tt-done-${event.event_id}`}><CheckCircle2 className="w-4 h-4" /> Selesai</span>
                : <Btn variant="ghost" size="sm" data-testid={`req-tt-${event.event_id}`} onClick={followTt}>Follow</Btn>}
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="font-heading font-bold text-sm flex items-center gap-2"><Facebook className="w-4 h-4 text-[#1877F2] fill-current" /> Follow Facebook {contact.facebook || "SkorUp.io"} <span className="text-[10px] text-su-muted font-black uppercase">(opsional)</span></span>
              {followedFb
                ? <span className="flex items-center gap-1 text-su-success font-heading font-black text-xs" data-testid={`req-fb-done-${event.event_id}`}><CheckCircle2 className="w-4 h-4" /> Selesai</span>
                : <Btn variant="ghost" size="sm" data-testid={`req-fb-${event.event_id}`} onClick={followFb}>Follow</Btn>}
            </div>
          </div>

          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" data-testid={`mega-file-input-${event.event_id}`} onChange={upload} />
          <Btn variant="secondary" data-testid={`mega-upload-${event.event_id}`} onClick={() => fileRef.current?.click()} disabled={uploading || !allDone} className="w-full">
            {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Mengunggah...</> : <><Upload className="w-4 h-4" /> Upload Screenshot Bukti (bisa banyak)</>}
          </Btn>
          {!allDone && <p className="text-xs text-su-muted text-center mt-2">Ceklis semua syarat di atas untuk membuka tombol upload.</p>}
          </div>
          )}

          {event.can_free && (
          <div className="flex items-center gap-3 px-2">
            <div className="flex-1 h-px bg-su-border" />
            <span className="text-xs font-heading font-black text-su-muted uppercase">atau</span>
            <div className="flex-1 h-px bg-su-border" />
          </div>
          )}

          <div className="card-chunky p-6" data-testid={`mega-paid-option-${event.event_id}`}>
            <span className="inline-flex items-center gap-1.5 bg-su-secondary/15 text-su-secondaryShadow px-3 py-1 rounded-full font-heading font-black text-xs mb-3">{event.can_free ? "OPSI 2 · BAYAR INSTAN ⚡" : "BELI TIKET ⚡"}</span>
            <div className="flex items-center gap-2 mb-1"><Ticket className="w-5 h-5 text-su-secondaryShadow" /><h3 className="font-heading font-black text-xl">{reentry ? "Beli Tiket untuk Ikut Lagi" : "Beli Tiket Langsung"}</h3></div>
            <p className="text-su-muted font-medium text-sm mb-4">{reentry ? "Buka tiket baru dan langsung kerjakan lagi Tryout Akbar ini—tanpa upload bukti, tanpa nunggu approval." : "Nggak sempat berbagi? Buka tiket seketika dan langsung ikut Tryout Akbar—tanpa upload bukti, tanpa nunggu approval."}</p>
            <div className="flex items-center gap-3 mb-4">
              <span className="inline-flex items-center gap-1.5 rounded-2xl bg-su-bg border-2 border-su-border px-4 py-2 font-heading font-black"><Coins className="w-5 h-5 text-su-secondaryShadow" /> {paidCost.token} Token</span>
              <span className="text-su-muted font-black">+</span>
              <span className="inline-flex items-center gap-1.5 rounded-2xl bg-su-bg border-2 border-su-border px-4 py-2 font-heading font-black"><Zap className="w-5 h-5 text-su-energy" /> {paidCost.energy} Energi</span>
            </div>
            <Btn variant="secondary" data-testid={`mega-pay-${event.event_id}`} onClick={unlockPaid} disabled={paying} className="w-full">
              {paying ? <><Loader2 className="w-4 h-4 animate-spin" /> Membuka tiket...</> : <><Ticket className="w-4 h-4" /> Buka Tiket ({paidCost.token} Token + {paidCost.energy} Energi)</>}
            </Btn>
          </div>
        </div>
      )}

      {event.rewards_distributed && (event.winners || []).length > 0 ? (
        <div className="card-chunky p-6" data-testid={`mega-champions-${event.event_id}`}>
          <h3 className="font-heading font-black text-xl mb-1 flex items-center gap-2"><Trophy className="w-5 h-5 text-su-secondary" /> Papan Juara Tryout Akbar 🏆</h3>
          <p className="text-su-muted font-medium text-sm mb-4">Event sudah ditutup. Inilah 10 besar pejuang terbaik yang berhak atas hadiah!</p>
          <div className="space-y-2">
            {event.winners.slice(0, 10).map((r) => {
              const isMe = r.user_id === user?.user_id;
              const medal = r.rank === 1 ? "bg-su-secondary text-black" : r.rank === 2 ? "bg-slate-300 text-black" : r.rank === 3 ? "bg-amber-700 text-white" : "bg-su-track text-su-muted";
              return (
                <div key={r.rank} className={`flex items-center gap-3 rounded-2xl p-3 border-2 ${isMe ? "border-su-primary bg-su-primary/5" : r.rank <= 3 ? "border-su-secondary/50 bg-su-secondary/5" : "border-su-border"}`}>
                  <span className={`w-9 h-9 grid place-items-center rounded-xl font-heading font-black text-sm shrink-0 ${medal}`}>{r.rank}</span>
                  <div className="min-w-0 flex-1">
                    <div className="font-heading font-extrabold truncate flex items-center gap-1">{r.name}{isMe && <span className="text-xs text-su-primary">(kamu)</span>}</div>
                    {r.reward_label && <div className="text-[11px] font-heading font-bold text-su-secondaryShadow flex items-center gap-1"><Trophy className="w-3 h-3" /> {r.reward_label}</div>}
                    {isMe && r.rank <= 3 && (
                      <button data-testid={`cert-open-${event.event_id}`} onClick={() => setCert({ name: r.name, rank: r.rank, score: r.score })}
                        className="mt-1 inline-flex items-center gap-1 text-[11px] font-heading font-black text-su-primary hover:underline">
                        <Award className="w-3 h-3" /> Unduh Sertifikat Juara
                      </button>
                    )}
                  </div>
                  <span className="font-mono font-bold text-su-primary shrink-0">{r.score}</span>
                </div>
              );
            })}
          </div>
          {cert && (
            <CertificateModal
              open={!!cert}
              onClose={() => setCert(null)}
              name={cert.name}
              rank={cert.rank}
              score={cert.score}
              eventTitle={event.title}
              exam={event.exam}
              dateIso={event.rewards_distributed_at}
            />
          )}
        </div>
      ) : (
        <div className="card-chunky p-6">
          <h3 className="font-heading font-black text-xl mb-1 flex items-center gap-2"><Trophy className="w-5 h-5 text-su-secondary" /> Peringkat Nasional Event</h3>
          <p className="text-su-muted font-medium text-sm mb-4">10 besar berhak atas hadiah otomatis saat event ditutup 🎁</p>
          {board.length === 0 ? (
            <p className="text-su-muted font-medium text-sm text-center py-6">Belum ada peserta yang menyelesaikan. Jadilah yang pertama!</p>
          ) : (
            <div className="space-y-2">
              {board.slice(0, 10).map((r) => (
                <div key={r.rank} className={`flex items-center gap-3 rounded-2xl p-3 border-2 ${r.is_me ? "border-su-primary bg-su-primary/5" : "border-su-border"}`}>
                  <span className={`w-8 h-8 grid place-items-center rounded-lg font-heading font-black text-sm shrink-0 ${r.rank === 1 ? "bg-su-secondary text-black" : r.rank === 2 ? "bg-slate-300 text-black" : r.rank === 3 ? "bg-amber-700 text-white" : "bg-su-track text-su-muted"}`}>{r.rank}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-extrabold truncate flex items-center gap-1">{r.name}{r.vip && <Crown className="w-3.5 h-3.5 text-su-secondary fill-current" />}{r.is_me && <span className="text-xs text-su-primary">(kamu)</span>}</div>
                    {r.reward_label && <div className="text-[11px] font-heading font-bold text-su-secondaryShadow flex items-center gap-1" data-testid={`mega-reward-${event.event_id}-${r.rank}`}><Gift className="w-3 h-3" /> {r.reward_label}</div>}
                  </div>
                  <span className="font-mono font-bold text-su-primary shrink-0">{r.score}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="text-center">
      <Icon className="w-5 h-5 text-su-primary mx-auto mb-1" />
      <div className="font-heading font-black">{value}</div>
      <div className="text-[10px] font-bold uppercase text-su-muted">{label}</div>
    </div>
  );
}
